import { createAsyncThunk, createReducer, createAction } from '@reduxjs/toolkit';
import { API, SCOPE_CRM_DASHBOARD } from '../../constants';
import { application } from '../../services/application';
import { buildDashboardFilterParams, EMPTY_DASHBOARD_FILTERS } from '../../containers/GovCrm/Contacts/helpers';

const INITIAL_LAST_TOUCH = {
  items: [],
  count: 0,
  order: ['lastContacted', 'asc'],
  pagination: { page: 1, perPage: 10 },
  pending: false,
  loaded: false,
};

const INITIAL_FILTER_OPTIONS = {
  total: 0,
  assignees: [],
  origins: [],
  createdWeeks: [],
  lastContactedWeeks: [],
  pending: false,
  loaded: false,
};

const INITIAL_REQUEST_IDS = { breakdowns: null, lastTouch: null, filterOptions: null };

const INITIAL_FILTERED_STATE = {
  filters: EMPTY_DASHBOARD_FILTERS,
  filterOptions: INITIAL_FILTER_OPTIONS,
  byStage: [],
  byOrigin: [],
  byType: [],
  lastTouch: INITIAL_LAST_TOUCH,
  breakdownsLoaded: false,
  breakdownsPending: false,
  breakdownsStale: false,
  breakdownsAppliedFilters: null,
  filtersError: false,
  requestErrors: { breakdowns: false, lastTouch: false, filterOptions: false },
  requestIds: INITIAL_REQUEST_IDS,
};

export const INITIAL_STATE = {
  weekStart: null,
  weekEnd: null,
  contactsAdded: 0,
  organizationsAdded: 0,
  contactsTouched: 0,
  notesThisWeek: 0,
  ...INITIAL_FILTERED_STATE,
  tagOptions: { items: [], pending: false, loaded: false },
  pending: false,
  loaded: false,
};

const getFilterParams = (getState) => buildDashboardFilterParams(getState().crmDashboard.filters);

export const fetchWeeklyKpis = createAsyncThunk(`${SCOPE_CRM_DASHBOARD}/fetchWeeklyKpis`, (params) => {
  return application.call(API.CRM_DASHBOARD.GET_WEEKLY_KPIS, params);
});

export const fetchContactBreakdowns = createAsyncThunk(
  `${SCOPE_CRM_DASHBOARD}/fetchContactBreakdowns`,
  async (_params, { getState }) => {
    const filters = getFilterParams(getState);
    const response = await application.call(API.CRM_DASHBOARD.GET_CONTACT_BREAKDOWNS, { filters });

    return { ...response, appliedFilters: filters };
  }
);

export const fetchContactsByLastTouch = createAsyncThunk(
  `${SCOPE_CRM_DASHBOARD}/fetchContactsByLastTouch`,
  (params = {}, { getState }) => {
    const { lastTouch } = getState().crmDashboard;

    return application.call(API.CRM_DASHBOARD.GET_CONTACTS_BY_LAST_TOUCH, {
      page: params.page ?? lastTouch.pagination.page,
      perPage: params.perPage ?? lastTouch.pagination.perPage,
      orderBy: params.orderBy ?? lastTouch.order[0],
      orderDir: params.orderDir ?? lastTouch.order[1],
      filters: getFilterParams(getState),
    });
  }
);

export const fetchFilterOptions = createAsyncThunk(
  `${SCOPE_CRM_DASHBOARD}/fetchFilterOptions`,
  (_params, { getState }) => {
    return application.call(API.CRM_DASHBOARD.GET_FILTER_OPTIONS, {
      filters: getFilterParams(getState),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  }
);

export const fetchTagOptions = createAsyncThunk(`${SCOPE_CRM_DASHBOARD}/fetchTagOptions`, async () => {
  const result = await application.call(API.CRM_COMPANY_TAGS.GET_LIST, {});

  return Array.isArray(result?.tags) ? result.tags : [];
});

export const setLastTouchOrder = createAction(`${SCOPE_CRM_DASHBOARD}/setLastTouchOrder`);
export const setLastTouchPage = createAction(`${SCOPE_CRM_DASHBOARD}/setLastTouchPage`);
export const setLastTouchPerPage = createAction(`${SCOPE_CRM_DASHBOARD}/setLastTouchPerPage`);
export const setDashboardFilter = createAction(`${SCOPE_CRM_DASHBOARD}/setDashboardFilter`);
export const removeDashboardFilterValue = createAction(`${SCOPE_CRM_DASHBOARD}/removeDashboardFilterValue`);
export const clearDashboardFilters = createAction(`${SCOPE_CRM_DASHBOARD}/clearDashboardFilters`);
export const resetDashboardFilters = createAction(`${SCOPE_CRM_DASHBOARD}/resetDashboardFilters`);

export const actions = {
  fetchWeeklyKpis,
  fetchContactBreakdowns,
  fetchContactsByLastTouch,
  fetchFilterOptions,
  fetchTagOptions,
  setLastTouchOrder,
  setLastTouchPage,
  setLastTouchPerPage,
  setDashboardFilter,
  removeDashboardFilterValue,
  clearDashboardFilters,
  resetDashboardFilters,
};

const isLatest = (state, key, action) => state.requestIds[key] === action.meta.requestId;

const setRequestError = (state, key, failed) => {
  state.requestErrors[key] = failed;
  state.filtersError = Object.values(state.requestErrors).some(Boolean);
};

export default createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(fetchWeeklyKpis.pending, (state) => {
      state.pending = true;
    })
    .addCase(fetchWeeklyKpis.fulfilled, (state, action) => {
      state.pending = false;
      state.loaded = true;
      state.weekStart = action.payload?.weekStart || null;
      state.weekEnd = action.payload?.weekEnd || null;
      state.contactsAdded = action.payload?.contactsAdded || 0;
      state.organizationsAdded = action.payload?.organizationsAdded || 0;
      state.contactsTouched = action.payload?.contactsTouched || 0;
      state.notesThisWeek = action.payload?.notesThisWeek || 0;
    })
    .addCase(fetchWeeklyKpis.rejected, (state) => {
      state.pending = false;
    })
    .addCase(fetchContactBreakdowns.pending, (state, action) => {
      state.pending = true;
      state.breakdownsPending = true;
      state.requestIds.breakdowns = action.meta.requestId;
    })
    .addCase(fetchContactBreakdowns.fulfilled, (state, action) => {
      if (!isLatest(state, 'breakdowns', action)) return;
      state.pending = false;
      state.breakdownsPending = false;
      state.breakdownsStale = false;
      setRequestError(state, 'breakdowns', false);
      state.breakdownsLoaded = true;
      state.byStage = action.payload?.byStage || [];
      state.byOrigin = action.payload?.byOrigin || [];
      state.byType = action.payload?.byType || [];
      state.breakdownsAppliedFilters = action.payload?.appliedFilters || null;
    })
    .addCase(fetchContactBreakdowns.rejected, (state, action) => {
      if (!isLatest(state, 'breakdowns', action)) return;
      state.pending = false;
      state.breakdownsPending = false;
      state.breakdownsStale = true;
      setRequestError(state, 'breakdowns', true);
    })
    .addCase(fetchContactsByLastTouch.pending, (state, action) => {
      state.lastTouch.pending = true;
      state.requestIds.lastTouch = action.meta.requestId;
    })
    .addCase(fetchContactsByLastTouch.fulfilled, (state, action) => {
      if (!isLatest(state, 'lastTouch', action)) return;
      state.lastTouch.pending = false;
      state.lastTouch.loaded = true;
      setRequestError(state, 'lastTouch', false);
      state.lastTouch.items = action.payload?.items || [];
      state.lastTouch.count = action.payload?.count || 0;
      state.lastTouch.order = [action.payload?.orderBy || 'lastContacted', action.payload?.orderDir || 'asc'];
      state.lastTouch.pagination = {
        page: action.payload?.page || 1,
        perPage: action.payload?.perPage || 10,
      };
    })
    .addCase(fetchContactsByLastTouch.rejected, (state, action) => {
      if (!isLatest(state, 'lastTouch', action)) return;
      state.lastTouch.pending = false;
      setRequestError(state, 'lastTouch', true);
    })
    .addCase(fetchFilterOptions.pending, (state, action) => {
      state.filterOptions.pending = true;
      state.requestIds.filterOptions = action.meta.requestId;
    })
    .addCase(fetchFilterOptions.fulfilled, (state, action) => {
      if (!isLatest(state, 'filterOptions', action)) return;
      setRequestError(state, 'filterOptions', false);
      state.filterOptions = {
        total: action.payload?.total || 0,
        assignees: action.payload?.assignees || [],
        origins: action.payload?.origins || [],
        createdWeeks: action.payload?.createdWeeks || [],
        lastContactedWeeks: action.payload?.lastContactedWeeks || [],
        pending: false,
        loaded: true,
      };
    })
    .addCase(fetchFilterOptions.rejected, (state, action) => {
      if (!isLatest(state, 'filterOptions', action)) return;
      state.filterOptions.pending = false;
      setRequestError(state, 'filterOptions', true);
    })
    .addCase(fetchTagOptions.pending, (state) => {
      state.tagOptions.pending = true;
    })
    .addCase(fetchTagOptions.fulfilled, (state, action) => {
      state.tagOptions = { items: action.payload, pending: false, loaded: true };
    })
    .addCase(fetchTagOptions.rejected, (state) => {
      state.tagOptions.pending = false;
    })
    .addCase(setLastTouchOrder, (state, action) => {
      const [orderBy, orderDir] = action.payload || [];
      state.lastTouch.order = [orderBy, orderDir];
      state.lastTouch.pagination.page = 1;
    })
    .addCase(setLastTouchPage, (state, action) => {
      state.lastTouch.pagination.page = action.payload;
    })
    .addCase(setLastTouchPerPage, (state, action) => {
      state.lastTouch.pagination.perPage = action.payload;
      state.lastTouch.pagination.page = 1;
    })
    .addCase(setDashboardFilter, (state, action) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
      state.lastTouch.pagination.page = 1;
    })
    .addCase(removeDashboardFilterValue, (state, action) => {
      const { key, value } = action.payload;
      state.filters[key] = (state.filters[key] || []).filter((item) => item !== value);
      state.lastTouch.pagination.page = 1;
    })
    .addCase(clearDashboardFilters, (state) => {
      state.filters = EMPTY_DASHBOARD_FILTERS;
      state.lastTouch.pagination.page = 1;
    })
    .addCase(resetDashboardFilters, (state) => ({ ...state, ...INITIAL_FILTERED_STATE }));
});
